import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './TermsConditions.css';

const TermsConditions: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('');
  const [isScrolling, setIsScrolling] = useState<boolean>(false);
  const [isTocCollapsed, setIsTocCollapsed] = useState<boolean>(false);
  const [showScrollTop, setShowScrollTop] = useState<boolean>(false);
  const location = useLocation();

  useEffect(() => {
    // Simple scroll-based active section detection
    const handleScroll = () => {
      // Skip if we're in the middle of a TOC click navigation
      if (isScrolling) return;

      const sections = document.querySelectorAll('.terms-section');
      const scrollTop = window.scrollY + 150; // Offset for fixed header
      
      // Show/hide scroll to top button for mobile
      setShowScrollTop(window.scrollY > 300);

      // Find the section that contains the current scroll position
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i] as HTMLElement;
        const sectionTop = section.offsetTop;

        if (scrollTop >= sectionTop) {
          const sectionId = section.id;
          if (sectionId !== activeSection) {
            setActiveSection(sectionId);
          }
          break;
        }
      }
    };

    // Add scroll listener
    window.addEventListener('scroll', handleScroll);

    // Initial scroll check
    setTimeout(handleScroll, 100);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [activeSection, isScrolling]);

  // Reset TOC highlight when page loads
  useEffect(() => {
    setActiveSection('');
    setIsScrolling(false);
    // Check active section after page loads
    setTimeout(() => {
      const sections = document.querySelectorAll('.terms-section');
      const scrollTop = window.scrollY + 150;
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i] as HTMLElement;
        const sectionTop = section.offsetTop;
        
        if (scrollTop >= sectionTop) {
          setActiveSection(section.id);
          break;
        }
      }
    }, 200);
  }, [location.pathname]);

  const scrollToSection = (sectionId: string) => {
    // Immediately set the active section
    setActiveSection(sectionId);
    setIsScrolling(true);

    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.location.hash = sectionId;

      // Re-enable scroll detection after animation completes
      setTimeout(() => {
        setIsScrolling(false);
      }, 800);
    }
  };

  const toggleToc = () => {
    setIsTocCollapsed(!isTocCollapsed);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const tableOfContents = [
    { id: 'our-services', title: '1. OUR SERVICES' },
    { id: 'intellectual-property', title: '2. INTELLECTUAL PROPERTY RIGHTS' },
    { id: 'user-representations', title: '3. USER REPRESENTATIONS' },
    { id: 'user-registration', title: '4. USER REGISTRATION' },
    { id: 'purchases-payment', title: '5. PURCHASES AND PAYMENT' },
    { id: 'subscriptions', title: '6. SUBSCRIPTIONS' },
    {
      id: 'software-ai',
      title: '7. SOFTWARE AND USE OF ARTIFICIAL INTELLIGENCE',
    },
    { id: 'prohibited-activities', title: '8. PROHIBITED ACTIVITIES' },
    { id: 'user-contributions', title: '9. USER GENERATED CONTRIBUTIONS' },
    { id: 'contribution-license', title: '10. CONTRIBUTION LICENSE' },
    { id: 'review-guidelines', title: '11. GUIDELINES FOR REVIEWS' },
    { id: 'mobile-license', title: '12. MOBILE APPLICATION LICENSE' },
    { id: 'social-media', title: '13. SOCIAL MEDIA' },
    { id: 'third-party', title: '14. THIRD-PARTY WEBSITES AND CONTENT' },
    { id: 'services-management', title: '15. SERVICES MANAGEMENT' },
    { id: 'privacy-policy', title: '16. PRIVACY POLICY' },
    { id: 'copyright', title: '17. COPYRIGHT INFRINGEMENTS' },
    { id: 'termination', title: '18. TERM AND TERMINATION' },
    { id: 'modifications', title: '19. MODIFICATIONS AND INTERRUPTIONS' },
    { id: 'governing-law', title: '20. GOVERNING LAW' },
    { id: 'dispute-resolution', title: '21. DISPUTE RESOLUTION' },
    { id: 'corrections', title: '22. CORRECTIONS' },
    { id: 'disclaimer', title: '23. DISCLAIMER' },
    { id: 'liability-limitations', title: '24. LIMITATIONS OF LIABILITY' },
    { id: 'indemnification', title: '25. INDEMNIFICATION' },
    { id: 'user-data', title: '26. USER DATA' },
    {
      id: 'electronic-communications',
      title: '27. ELECTRONIC COMMUNICATIONS, TRANSACTIONS, AND SIGNATURES',
    },
    { id: 'california-users', title: '28. CALIFORNIA USERS AND RESIDENTS' },
    { id: 'miscellaneous', title: '29. MISCELLANEOUS' },
    { id: 'contact-us', title: '30. CONTACT US' },
  ];

  return (
    <div className="terms-conditions-container">
      <Helmet>
        <title>Terms &amp; Conditions | Morph</title>
        <meta
          name="description"
          content="The legal terms governing your use of Morph's AI home inspection software and Services."
        />
        <link
          rel="canonical"
          href="https://www.morph-technologies.com/terms-conditions"
        />
      </Helmet>
      <div className="terms-conditions-header">
        <h1>TERMS AND CONDITIONS</h1>
        <p className="last-updated">Last updated August 5, 2025</p>
      </div>

      <div className="terms-conditions-layout">
        <aside className={`terms-table-of-contents ${isTocCollapsed ? 'collapsed' : ''}`}>
          <div className="terms-toc-header">
            <h2>TABLE OF CONTENTS</h2>
            <button className="terms-toc-toggle" onClick={toggleToc}>
              {isTocCollapsed ? '▶' : '◀'}
            </button>
          </div>
          {!isTocCollapsed && (
            <nav>
              {tableOfContents.map((item) => (
                <button
                  key={item.id}
                  className={`terms-toc-item ${activeSection === item.id ? 'active' : ''}`}
                  onClick={() => scrollToSection(item.id)}
                >
                  {item.title}
                </button>
              ))}
            </nav>
          )}
        </aside>

        <div className="terms-conditions-content">
          <div className="terms-agreement-section">
            <h2>AGREEMENT TO OUR LEGAL TERMS</h2>
            <p>
              We are Morph Technologies LLC ("Company," "we," "us," "our"), a
              company registered in Washington, United States at 2690 152nd Ave
              NE, Unit 313, Redmond, WA 98052.
            </p>
            <p>
              We operate the website{' '}
              <a href="https://www.morph-technologies.com">
                https://www.morph-technologies.com
              </a>{' '}
              (the "Site"), the mobile application Morph Inspect (the "App"), as
              well as any other related products and services that refer or link
              to these legal terms (the "Legal Terms") (collectively, the
              "Services").
            </p>
            <p>
              We provide an AI-assisted platform for home inspectors to generate
              reports using artificial intelligence and machine learning
              technologies.
            </p>
            <p>
              You can contact us by phone at{' '}
              <a href="tel:1-213-357-3951">1-213-357-3951</a>, email at
              <a href="mailto:info@morph-technologies.com">
                info@morph-technologies.com
              </a>
              , or by mail to 2690 152nd Ave NE, Unit 313, Redmond, WA 98052,
              United States.
            </p>
            <p>
              <strong>
                These Legal Terms constitute a legally binding agreement made
                between you, whether personally or on behalf of an entity
                ("you"), and Morph Technologies LLC, concerning your access to
                and use of the Services. You agree that by accessing the
                Services, you have read, understood, and agreed to be bound by
                all of these Legal Terms. IF YOU DO NOT AGREE WITH ALL OF THESE
                LEGAL TERMS, THEN YOU ARE EXPRESSLY PROHIBITED FROM USING THE
                SERVICES AND YOU MUST DISCONTINUE USE IMMEDIATELY.
              </strong>
            </p>
            <p>
              We will provide you with prior notice of any scheduled changes to
              the Services you are using. The modified Legal Terms will become
              effective upon posting or notifying you by
              info@morph-technologies.com, as stated in the email message. By
              continuing to use the Services after the effective date of any
              changes, you agree to be bound by the modified terms.
            </p>
            <p>
              <strong>
                The Services are intended for users who are at least 18 years
                old. Persons under the age of 18 are not permitted to use or
                register for the Services.
              </strong>
            </p>
            <p>
              We recommend that you print a copy of these Legal Terms for your
              records.
            </p>
          </div>

          <section id="our-services" className="terms-section">
            <h2>1. OUR SERVICES</h2>
            <p>
              The information provided when using the Services is not intended
              for distribution to or use by any person or entity in any
              jurisdiction or country where such distribution or use would be
              contrary to law or regulation or which would subject us to any
              registration requirement within such jurisdiction or country.
              Accordingly, those persons who choose to access the Services from
              other locations do so on their own initiative and are solely
              responsible for compliance with local laws, if and to the extent
              local laws are applicable.
            </p>
            <p>
              Morph Inspect is an AI-powered Software-as-a-Service (SaaS)
              platform designed specifically for licensed home inspectors. Our
              Services provide:
            </p>
            <ul>
              <li>
                <strong>AI-Assisted Report Generation:</strong> Automated
                creation of comprehensive home inspection reports using
                artificial intelligence
              </li>
              <li>
                <strong>Property Analysis Tools:</strong> Advanced tools for
                analyzing property conditions and identifying potential issues
              </li>
              <li>
                <strong>Report Templates:</strong> Customizable report templates
                that comply with industry standards
              </li>
              <li>
                <strong>Photo and Media Management:</strong> Organization and
                analysis of inspection photos and videos
              </li>
              <li>
                <strong>Client Management:</strong> Tools for managing
                inspection appointments and client communications
              </li>
              <li>
                <strong>Compliance Assistance:</strong> Features to help ensure
                reports meet local and national inspection standards
              </li>
            </ul>
            <p>
              The Services are not tailored to comply with industry-specific
              regulations (Health Insurance Portability and Accountability Act
              (HIPAA), Federal Information Security Management Act (FISMA),
              etc.), so if your interactions would be subjected to such laws,
              you may not use the Services. You may not use the Services in a
              way that would violate the Gramm-Leach-Bliley Act (GLBA).
            </p>
          </section>

          <section id="intellectual-property" className="terms-section">
            <h2>2. INTELLECTUAL PROPERTY RIGHTS</h2>

            <h3>Our intellectual property</h3>
            <p>
              We are the owner or the licensee of all intellectual property
              rights in our Services, including all source code, databases,
              functionality, software, website designs, audio, video, text,
              photographs, AI models, algorithms, and graphics in the Services
              (collectively, the "Content"), as well as the trademarks, service
              marks, and logos contained therein (the "Marks").
            </p>
            <p>
              Our Content and Marks are protected by copyright and trademark
              laws (and various other intellectual property rights and unfair
              competition laws) and treaties in the United States and around the
              world.
            </p>
            <p>
              The Content and Marks are provided in or through the Services "AS
              IS" for your personal, non-commercial use or internal business
              purpose only.
            </p>

            <h3>Your use of our Services</h3>
            <p>
              Subject to your compliance with these Legal Terms, including the
              "PROHIBITED ACTIVITIES" section below, we grant you a
              non-exclusive, non-transferable, revocable license to:
            </p>
            <ul>
              <li>access the Services; and</li>
              <li>download and store reports created through the Services,</li>
              <li>Use our mobile application on your authorized devices</li>
            </ul>
            <p>solely for your personal or internal business purpose.</p>

            <p>
              Except as set out in this section or elsewhere in our Legal Terms,
              no part of the Services and no Content or Marks may be copied,
              reproduced, aggregated, republished, uploaded, posted, publicly
              displayed, encoded, translated, transmitted, distributed, sold,
              licensed, or otherwise exploited for any commercial purpose
              whatsoever, without our express prior written permission.
            </p>

            <h3>Reports You Create</h3>
            <p>
              You retain ownership of the inspection reports you create using
              our Services, subject to the following:
            </p>
            <ul>
              <li>
                You grant us a limited license to process your data to provide
                the Services
              </li>
              <li>
                You are responsible for the accuracy and completeness of all
                information you input
              </li>
              <li>
                You must ensure all generated reports are reviewed and validated
                before delivery to clients
              </li>
            </ul>

            <h3>AI-Generated Content</h3>
            <p>
              Content generated by our AI systems is provided "as-is" and
              requires your professional review and validation before use in any
              official capacity.
            </p>

            <h3>Restrictions</h3>
            <p>You may not:</p>
            <ul>
              <li>
                Reverse engineer, decompile, or attempt to extract our
                proprietary AI algorithms
              </li>
              <li>
                Use our Services to create competing inspection software
                platforms
              </li>
              <li>
                Share your account credentials with unlicensed individuals
              </li>
              <li>Use our Services for any unlawful or unauthorized purpose</li>
            </ul>
            <p>
              Any breach of these Intellectual Property Rights will constitute a
              material breach of our Legal Terms and your right to use our
              Services will terminate immediately.
            </p>
          </section>

          <section id="user-representations" className="terms-section">
            <h2>3. USER REPRESENTATIONS</h2>
            <p>By using the Services, you represent and warrant that:</p>
            <ol>
              <li>
                all registration information you submit will be true, accurate,
                current, and complete;
              </li>
              <li>
                you will maintain the accuracy of such information and promptly
                update such registration information as necessary;
              </li>
              <li>
                you have the legal capacity and you agree to comply with these
                Legal Terms;
              </li>
              <li>
                you are not a minor in the jurisdiction in which you reside;
              </li>
              <li>
                you will not access the Services through automated or non-human
                means, whether through a bot, script or otherwise;
              </li>
              <li>
                you will not use the Services for any illegal or unauthorized
                purpose;
              </li>
              <li>
                your use of the Services will not violate any applicable law or
                regulation;
              </li>
              <li>
                you are a licensed home inspector, certified building inspector,
                or other qualified professional authorized to conduct property
                inspections in your jurisdiction; and
              </li>
              <li>
                you understand that AI-generated content requires professional
                review and validation.
              </li>
            </ol>
            <p>
              If you provide any information that is untrue, inaccurate, not
              current, or incomplete, we have the right to suspend or terminate
              your account and refuse any and all current or future use of the
              Services (or any portion thereof).
            </p>
          </section>

          <section id="user-registration" className="terms-section">
            <h2>4. USER REGISTRATION</h2>
            <p>
              You may be required to register to use the Services. You agree to
              keep your password confidential and will be responsible for all
              use of your account and password. We reserve the right to remove,
              reclaim, or change a username you select if we determine, in our
              sole discretion, that such username is inappropriate, obscene, or
              otherwise objectionable.
            </p>
            <p>
              To access certain features of the Services, you must register for
              an account. During registration, you must provide:
            </p>
            <ul>
              <li>Your professional license information</li>
              <li>Business contact information</li>
              <li>Valid email address and phone number</li>
              <li>Proof of professional insurance (may be required)</li>
            </ul>
            <p>
              <strong>Professional Verification:</strong> We reserve the right
              to verify your professional credentials and may suspend accounts
              pending verification.
            </p>
          </section>

          <section id="purchases-payment" className="terms-section">
            <h2>5. PURCHASES AND PAYMENT</h2>
            <p>We accept the following forms of payment:</p>
            <ul className="payment-methods">
              <li>Discover</li>
              <li>American Express</li>
              <li>Visa</li>
              <li>Mastercard</li>
            </ul>
            <p>
              You agree to provide current, complete, and accurate purchase and
              account information for all purchases made via the Services. You
              further agree to promptly update account and payment information,
              including email address, payment method, and payment card
              expiration date, so that we can complete your transactions and
              contact you as needed. Sales tax will be added to the price of
              purchases as deemed required by us. We may change prices at any
              time. All payments shall be in US dollars.
            </p>
            <p>
              You agree to pay all charges at the prices then in effect for your
              purchases and any applicable shipping fees, and you authorize us
              to charge your chosen payment provider for any such amounts upon
              placing your order. We reserve the right to correct any errors or
              mistakes in pricing, even if we have already requested or received
              payment.
            </p>
            <p>
              We reserve the right to refuse any order placed through the
              Services. We may, in our sole discretion, limit or cancel
              quantities purchased per person, per household, or per order.
              These restrictions may include orders placed by or under the same
              customer account, the same payment method, and/or orders that use
              the same billing or shipping address. We reserve the right to
              limit or prohibit orders that, in our sole judgment, appear to be
              placed by dealers, resellers, or distributors.
            </p>
          </section>

          <section id="subscriptions" className="terms-section">
            <h2>6. SUBSCRIPTIONS</h2>

            <h3>Billing and Renewal</h3>
            <p>
              Your subscription will continue and automatically renew unless
              canceled. You consent to our charging your payment method on a
              recurring basis without requiring your prior approval for each
              recurring charge, until such time as you cancel the applicable
              order. The length of your billing cycle will depend on the type of
              subscription plan you choose when you subscribed to the Services.
            </p>
            <div className="important-notice">
              <p>
                <strong>
                  IF YOU DO NOT WANT TO CONTINUE TO BE CHARGED ON A RECURRING
                  MONTHLY BASIS, YOU MUST CANCEL THE APPLICABLE PAID SERVICE
                  THROUGH YOUR ACCOUNT SETTINGS OR TERMINATE YOUR ACCOUNT BEFORE
                  THE END OF THE RECURRING TERM. PAID SERVICES CANNOT BE
                  TERMINATED BEFORE THE END OF THE PERIOD FOR WHICH YOU HAVE
                  ALREADY PAID, AND EXCEPT AS EXPRESSLY PROVIDED IN THESE TERMS,
                  WE WILL NOT REFUND ANY FEES THAT YOU HAVE ALREADY PAID.
                </strong>
              </p>
            </div>

            <h3>Free Trial</h3>
            <p>
              We offer a 14-day free trial to new users who register with the
              Services. The account will be charged according to the user's
              chosen subscription at the end of the free trial.
            </p>

            <h3>Cancellation</h3>
            <p>
              You can cancel your subscription at any time by logging into your
              account. Your cancellation will take effect at the end of the
              current paid term. If you have any questions or are unsatisfied
              with our Services, please email us at
              <a href="mailto:info@morph-technologies.com">
                info@morph-technologies.com
              </a>
              .
            </p>

            <h3>Fee Changes</h3>
            <p>
              We may, from time to time, make changes to the subscription fee
              and will communicate any price changes to you in accordance with
              applicable law.
            </p>
          </section>

          <section id="software-ai" className="terms-section">
            <h2>7. SOFTWARE AND USE OF ARTIFICIAL INTELLIGENCE</h2>
            <p>
              We may include software for use in connection with our Services.
              If such software is accompanied by an end user license agreement
              ("EULA"), the terms of the EULA will govern your use of the
              software. If such software is not accompanied by a EULA, then we
              grant to you a non-exclusive, revocable, personal, and
              non-transferable license to use such software solely in connection
              with our services and in accordance with these Legal Terms. Any
              software and any related documentation is provided "AS IS" without
              warranty of any kind, either express or implied, including,
              without limitation, the implied warranties of merchantability,
              fitness for a particular purpose, or non-infringement. You accept
              any and all risk arising out of use or performance of any
              software. You may not reproduce or redistribute any software
              except in accordance with the EULA or these Legal Terms.
            </p>
            <p>
              Our Services utilize artificial intelligence and machine learning
              technologies provided by third-party providers including Google
              Cloud AI, Anthropic, and OpenAI to analyze inspection data and
              generate reports. By using our Services, you acknowledge that AI
              technology assists in report generation and data analysis.
            </p>
          </section>

          <section id="prohibited-activities" className="terms-section">
            <h2>8. PROHIBITED ACTIVITIES</h2>
            <p>
              You may not access or use the Services for any purpose other than
              that for which we make the Services available. The Services may
              not be used in connection with any commercial endeavors except
              those that are specifically endorsed or approved by us.
            </p>
            <p>As a user of the Services, you agree not to:</p>
            <ul>
              <li>
                Systematically retrieve data or other content from the Services
                to create or compile, directly or indirectly, a collection,
                compilation, database, or directory without written permission
                from us.
              </li>
              <li>
                Trick, defraud, or mislead us and other users, especially in any
                attempt to learn sensitive account information such as user
                passwords.
              </li>
              <li>
                Circumvent, disable, or otherwise interfere with
                security-related features of the Services, including features
                that prevent or restrict the use or copying of any Content or
                enforce limitations on the use of the Services and/or the
                Content contained therein.
              </li>
              <li>
                Disparage, tarnish, or otherwise harm, in our opinion, us and/or
                the Services.
              </li>
              <li>
                Use any information obtained from the Services in order to
                harass, abuse, or harm another person.
              </li>
              <li>
                Make improper use of our support services or submit false
                reports of abuse or misconduct.
              </li>
              <li>
                Use the Services in a manner inconsistent with any applicable
                laws or regulations.
              </li>
              <li>
                Engage in unauthorized framing of or linking to the Services.
              </li>
              <li>
                Upload or transmit (or attempt to upload or to transmit)
                viruses, Trojan horses, or other material, including excessive
                use of capital letters and spamming (continuous posting of
                repetitive text), that interferes with any party's uninterrupted
                use and enjoyment of the Services or modifies, impairs,
                disrupts, alters, or interferes with the use, features,
                functions, operation, or maintenance of the Services.
              </li>
              <li>
                Engage in any automated use of the system, such as using scripts
                to send comments or messages, or using any data mining, robots,
                or similar data gathering and extraction tools.
              </li>
              <li>
                Delete the copyright or other proprietary rights notice from any
                Content.
              </li>
              <li>
                Attempt to impersonate another user or person or use the
                username of another user.
              </li>
              <li>
                Upload or transmit (or attempt to upload or to transmit) any
                material that acts as a passive or active information collection
                or transmission mechanism, including without limitation, clear
                graphics interchange formats ("gifs"), 1×1 pixels, web bugs,
                cookies, or other similar devices (sometimes referred to as
                "spyware" or "passive collection mechanisms" or "pcms").
              </li>
              <li>
                Interfere with, disrupt, or create an undue burden on the
                Services or the networks or services connected to the Services.
              </li>
              <li>
                Harass, annoy, intimidate, or threaten any of our employees or
                agents engaged in providing any portion of the Services to you.
              </li>
              <li>
                Attempt to bypass any measures of the Services designed to
                prevent or restrict access to the Services, or any portion of
                the Services.
              </li>
              <li>
                Copy or adapt the Services' software, including but not limited
                to Flash, PHP, HTML, JavaScript, or other code.
              </li>
              <li>
                Except as permitted by applicable law, decipher, decompile,
                disassemble, or reverse engineer any of the software comprising
                or in any way making up a part of the Services.
              </li>
              <li>
                Except as may be the result of standard search engine or
                Internet browser usage, use, launch, develop, or distribute any
                automated system, including without limitation, any spider,
                robot, cheat utility, scraper, or offline reader that accesses
                the Services, or use or launch any unauthorized script or other
                software.
              </li>
              <li>
                Use a buying agent or purchasing agent to make purchases on the
                Services.
              </li>
              <li>
                Make any unauthorized use of the Services, including collecting
                usernames and/or email addresses of users by electronic or other
                means for the purpose of sending unsolicited email, or creating
                user accounts by automated means or under false pretenses.
              </li>
              <li>
                Use the Services as part of any effort to compete with us or
                otherwise use the Services and/or the Content for any
                revenue-generating endeavor or commercial enterprise.
              </li>
              <li>Sell or otherwise transfer your profile.</li>
              <li>
                Use the Services to advertise or offer to sell goods and
                services.
              </li>
              <li>
                Upload contents that are illegal, fraudulent, or abusive in
                nature.
              </li>
              <li>
                Violates any third-party rights, including intellectual property
                rights.
              </li>
              <li>
                Attempts to create a competing product using our product without
                our written authorization.
              </li>
            </ul>
          </section>

          <section id="user-contributions" className="terms-section">
            <h2>9. USER GENERATED CONTRIBUTIONS</h2>
            <p>
              The Services allow you to create, submit, post, display, transmit,
              perform, publish, distribute, or broadcast content and materials,
              including but not limited to:
            </p>
            <ul>
              <li>Inspection reports and related documentation</li>
              <li>Property photographs and videos</li>
              <li>Client information and contact details</li>
              <li>Notes, observations, and professional commentary</li>
              <li>Custom report templates and forms</li>
            </ul>

            <h3>Responsibility for Content</h3>
            <p>
              You are solely responsible for any content you create, upload, or
              share through the Services. You represent and warrant that:
            </p>
            <ul>
              <li>
                You own or have the necessary rights to use all content you
                submit
              </li>
              <li>Your content does not violate any third-party rights</li>
              <li>
                You have obtained necessary permissions to photograph or
                document properties
              </li>
              <li>
                Your content complies with all applicable laws and professional
                standards
              </li>
            </ul>

            <h3>Client Consent</h3>
            <p>
              You represent that you have obtained appropriate consent from
              property owners or authorized representatives to:
            </p>
            <ul>
              <li>Photograph and document their property</li>
              <li>
                Store and process property information through our Services
              </li>
              <li>Include property details in inspection reports</li>
            </ul>
            <p>
              Any use of the Services in violation of the foregoing violates
              these Legal Terms and may result in, among other things,
              termination or suspension of your rights to use the Services.
            </p>
          </section>

          <section id="contribution-license" className="terms-section">
            <h2>10. CONTRIBUTION LICENSE</h2>
            <p>
              By posting your Contributions to any part of the Services, you
              automatically grant, and you represent and warrant that you have
              the right to grant, to us an unrestricted, unlimited, irrevocable,
              perpetual, non-exclusive, transferable, royalty-free, fully-paid,
              worldwide right, and license to host, use, copy, reproduce,
              disclose, sell, resell, publish, broadcast, retitle, archive,
              store, cache, publicly perform, publicly display, reformat,
              translate, transmit, excerpt (in whole or in part), and distribute
              such Contributions (including, without limitation, your image and
              voice) for any purpose, commercial, advertising, or otherwise, and
              to prepare derivative works of, or incorporate into other works,
              such Contributions, and grant and authorize sublicenses of the
              foregoing. The use and distribution may occur in any media formats
              and through any media channels.
            </p>
          </section>

          <section id="review-guidelines" className="terms-section">
            <h2>11. GUIDELINES FOR REVIEWS</h2>
            <p>
              We may provide you areas on the Services to leave reviews or
              ratings. When posting a review, you must comply with the following
              criteria:
            </p>
            <ul>
              <li>
                You should have firsthand experience with the person/entity
                being reviewed;
              </li>
              <li>
                Your reviews should not contain offensive profanity, or abusive,
                racist, offensive, or hateful language;
              </li>
              <li>
                Your reviews should not contain discriminatory references based
                on religion, race, gender, national origin, age, marital status,
                sexual orientation, or disability;
              </li>
              <li>
                Your reviews should not contain references to illegal activity;
              </li>
              <li>
                You should not be affiliated with competitors if posting
                negative reviews;
              </li>
              <li>
                You should not make any conclusions as to the legality of
                conduct;
              </li>
              <li>You may not post any false or misleading statements;</li>
              <li>
                You may not organize a campaign encouraging others to post
                reviews, whether positive or negative.
              </li>
            </ul>
          </section>

          <section id="mobile-license" className="terms-section">
            <h2>12. MOBILE APPLICATION LICENSE</h2>

            <h3>Use License</h3>
            <p>
              If you access the Services via the Morph Inspect mobile
              application, then we grant you a revocable, non-exclusive,
              non-transferable, limited right to install and use the mobile
              application on wireless electronic devices owned or controlled by
              you, and to access and use the mobile application on such devices
              strictly in accordance with the terms and conditions of this
              mobile application license contained in these Legal Terms.
            </p>

            <h3>You shall not:</h3>
            <ul>
              <li>
                Except as permitted by applicable law, decompile, reverse
                engineer, disassemble, attempt to derive the source code of, or
                decrypt the application;
              </li>
              <li>
                Make any modification, adaptation, improvement, enhancement,
                translation, or derivative work from the application;
              </li>
              <li>
                Violate any applicable laws, rules, or regulations in connection
                with your access or use of the application;
              </li>
              <li>
                Remove, alter, or obscure any proprietary notice (including any
                notice of copyright or trademark) posted by us or the licensors
                of the application;
              </li>
              <li>
                Use the application for any revenue-generating endeavor,
                commercial enterprise, or other purpose for which it is not
                designed or intended;
              </li>
              <li>
                Make the application available over a network or other
                environment permitting access or use by multiple devices or
                users at the same time;
              </li>
              <li>
                Use the application for creating a product, service, or software
                that is, directly or indirectly, competitive with or in any way
                a substitute for the application;
              </li>
              <li>
                Use the application to send automated queries to any website or
                to send any unsolicited commercial email;
              </li>
              <li>
                Use any proprietary information or any of our interfaces or our
                other intellectual property in the design, development,
                manufacture, licensing, or distribution of any applications,
                accessories, or devices for use with the application.
              </li>
            </ul>
          </section>

          <section id="social-media" className="terms-section">
            <h2>13. SOCIAL MEDIA</h2>
            <p>
              As part of the functionality of the Services, you may link your
              account with online accounts you have with third-party service
              providers (each such account, a "Third-Party Account") by either:
              (1) providing your Third-Party Account login information through
              the Services; or (2) allowing us to access your Third-Party
              Account, as is permitted under the applicable terms and conditions
              that govern your use of each Third-Party Account.
            </p>
          </section>

          <section id="third-party" className="terms-section">
            <h2>14. THIRD-PARTY WEBSITES AND CONTENT</h2>
            <p>
              The Services may contain (or you may be sent via the Site) links
              to other websites ("Third-Party Websites") as well as articles,
              photographs, text, graphics, pictures, designs, music, sound,
              video, information, applications, software, and other content or
              items belonging to or originating from third parties ("Third-Party
              Content"). Such Third-Party Websites and Third-Party Content are
              not investigated, monitored, or checked for accuracy,
              appropriateness, or completeness by us, and we are not responsible
              for any Third-Party Websites accessed through the Services or any
              Third-Party Content posted on, available through, or installed
              from the Services.
            </p>
          </section>

          <section id="services-management" className="terms-section">
            <h2>15. SERVICES MANAGEMENT</h2>
            <p>
              We reserve the right, but not the obligation, to: (1) monitor the
              Services for violations of these Legal Terms; (2) take appropriate
              legal action against anyone who, in our sole discretion, violates
              the law or these Legal Terms, including without limitation,
              reporting such user to law enforcement authorities; (3) in our
              sole discretion and without limitation, refuse, restrict access
              to, limit the availability of, or disable (to the extent
              technologically feasible) any of your Contributions or any portion
              thereof; (4) in our sole discretion and without limitation,
              notice, or liability, to remove from the Services or otherwise
              disable all files and content that are excessive in size or are in
              any way burdensome to our systems; and (5) otherwise manage the
              Services in a manner designed to protect our rights and property
              and to facilitate the proper functioning of the Services.
            </p>
          </section>

          <section id="privacy-policy" className="terms-section">
            <h2>16. PRIVACY POLICY</h2>
            <p>
              We care about data privacy and security. Please review our Privacy
              Policy:
              <a href="/privacy-policy">
                https://www.morph-technologies.com/privacy-policy
              </a>
              . By using the Services, you agree to be bound by our Privacy
              Policy, which is incorporated into these Legal Terms. Please be
              advised the Services are hosted in the United States. If you
              access the Services from any other region of the world with laws
              or other requirements governing personal data collection, use, or
              disclosure that differ from applicable laws in the United States,
              then through your continued use of the Services, you are
              transferring your data to the United States, and you expressly
              consent to have your data transferred to and processed in the
              United States.
            </p>
          </section>

          <section id="copyright" className="terms-section">
            <h2>17. COPYRIGHT INFRINGEMENTS</h2>
            <p>
              We respect the intellectual property rights of others. If you
              believe that any material available on or through the Services
              infringes upon any copyright you own or control, please
              immediately refer to the "DMCA NOTICE AND TAKEDOWN PROCEDURE"
              section below.
            </p>
          </section>

          <section id="termination" className="terms-section">
            <h2>18. TERM AND TERMINATION</h2>
            <p>
              These Legal Terms shall remain in full force and effect while you
              use the Services. WITHOUT LIMITING ANY OTHER PROVISION OF THESE
              LEGAL TERMS, WE RESERVE THE RIGHT TO, IN OUR SOLE DISCRETION AND
              WITHOUT NOTICE OR LIABILITY, DENY ACCESS TO AND USE OF THE
              SERVICES (INCLUDING BLOCKING CERTAIN IP ADDRESSES), TO ANY PERSON
              FOR ANY REASON OR FOR NO REASON, INCLUDING WITHOUT LIMITATION FOR
              BREACH OF ANY REPRESENTATION, WARRANTY, OR COVENANT CONTAINED IN
              THESE LEGAL TERMS OR OF ANY APPLICABLE LAW OR REGULATION. WE MAY
              TERMINATE YOUR USE OR PARTICIPATION IN THE SERVICES OR DELETE YOUR
              ACCOUNT AND ANY CONTENT OR INFORMATION THAT YOU POSTED AT ANY
              TIME, WITHOUT WARNING, IN OUR SOLE DISCRETION.
            </p>
          </section>

          <section id="modifications" className="terms-section">
            <h2>19. MODIFICATIONS AND INTERRUPTIONS</h2>
            <p>
              We reserve the right to change, modify, or remove the contents of
              the Services at any time or for any reason at our sole discretion
              without notice. However, we have no obligation to update any
              information on our Services. We will not be liable to you or any
              third party for any modification, price change, suspension, or
              discontinuance of the Services.
            </p>
          </section>

          <section id="governing-law" className="terms-section">
            <h2>20. GOVERNING LAW</h2>
            <p>
              These Legal Terms shall be governed by and defined following the
              laws of Washington. Morph Technologies LLC and yourself
              irrevocably consent that the courts of Washington shall have
              exclusive jurisdiction to resolve any dispute which may arise in
              connection with these Legal Terms.
            </p>
          </section>

          <section id="dispute-resolution" className="terms-section">
            <h2>21. DISPUTE RESOLUTION</h2>
            <p>
              Most disputes can be resolved without resort to litigation. For
              any concern or dispute you may have, you agree to first try to
              resolve the dispute informally by contacting us at the contact
              information provided in these Legal Terms.
            </p>
          </section>

          <section id="corrections" className="terms-section">
            <h2>22. CORRECTIONS</h2>
            <p>
              There may be information on the Services that contains
              typographical errors, inaccuracies, or omissions, including
              descriptions, pricing, availability, and various other
              information. We reserve the right to correct any errors,
              inaccuracies, or omissions and to change or update the information
              on the Services at any time, without prior notice.
            </p>
          </section>

          <section id="disclaimer" className="terms-section">
            <h2>23. DISCLAIMER</h2>
            <div className="important-notice">
              <p>
                <strong>
                  THE SERVICES ARE PROVIDED ON AN AS-IS AND AS-AVAILABLE BASIS.
                  YOU AGREE THAT YOUR USE OF THE SERVICES WILL BE AT YOUR SOLE
                  RISK. TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL
                  WARRANTIES, EXPRESS OR IMPLIED, IN CONNECTION WITH THE
                  SERVICES AND YOUR USE THEREOF.
                </strong>
              </p>
            </div>
            <p>
              <strong>PROFESSIONAL SERVICES DISCLAIMER:</strong> THE SERVICES DO
              NOT CONSTITUTE PROFESSIONAL INSPECTION SERVICES. WE DO NOT PROVIDE
              HOME INSPECTION SERVICES OR PROFESSIONAL ADVICE. ALL PROFESSIONAL
              JUDGMENTS AND DECISIONS REMAIN YOUR RESPONSIBILITY AS A LICENSED
              PROFESSIONAL.
            </p>
            <p>
              <strong>AI TECHNOLOGY DISCLAIMER:</strong> AI-GENERATED CONTENT IS
              PROVIDED FOR ASSISTANCE ONLY AND DOES NOT GUARANTEE ACCURACY,
              COMPLETENESS, OR COMPLIANCE WITH PROFESSIONAL STANDARDS. ALL AI
              OUTPUT REQUIRES PROFESSIONAL REVIEW AND VALIDATION.
            </p>
          </section>

          <section id="liability-limitations" className="terms-section">
            <h2>24. LIMITATIONS OF LIABILITY</h2>
            <p>
              IN NO EVENT WILL WE OR OUR DIRECTORS, EMPLOYEES, OR AGENTS BE
              LIABLE TO YOU OR ANY THIRD PARTY FOR ANY DIRECT, INDIRECT,
              CONSEQUENTIAL, EXEMPLARY, INCIDENTAL, SPECIAL, OR PUNITIVE
              DAMAGES, INCLUDING BUT NOT LIMITED TO, DAMAGES FOR LOSS OF
              PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES, ARISING
              OUT OF OR RELATING TO YOUR USE OF THE SERVICES.
            </p>
          </section>

          <section id="indemnification" className="terms-section">
            <h2>25. INDEMNIFICATION</h2>
            <p>
              You agree to defend, indemnify, and hold us harmless, including
              our subsidiaries, affiliates, and all of our respective officers,
              agents, partners, and employees, from and against any loss,
              damage, liability, claim, or demand, including reasonable
              attorneys' fees and expenses, made by any third party due to or
              arising out of: (1) your Contributions; (2) use of the Services;
              (3) breach of these Legal Terms; (4) any breach of your
              representations and warranties set forth in these Legal Terms; (5)
              your violation of the rights of a third party, including but not
              limited to intellectual property rights; or (6) any overt harmful
              act toward any other user of the Services with whom you connected
              via the Services.
            </p>
          </section>

          <section id="user-data" className="terms-section">
            <h2>26. USER DATA</h2>
            <p>
              We will maintain certain data that you transmit to the Services
              for the purpose of managing the performance of the Services, as
              well as data relating to your use of the Services. Although we
              perform regular routine backups of data, you are solely
              responsible for all data that you transmit or that relates to any
              activity you have undertaken using the Services.
            </p>
          </section>

          <section id="electronic-communications" className="terms-section">
            <h2>27. ELECTRONIC COMMUNICATIONS, TRANSACTIONS, AND SIGNATURES</h2>
            <p>
              Visiting the Services, sending us emails, and completing online
              forms constitute electronic communications. You consent to receive
              electronic communications, and you agree that all agreements,
              notices, disclosures, and other communications we provide to you
              electronically, via email and on the Services, satisfy any legal
              requirement that such communication be in writing.
            </p>
          </section>

          <section id="california-users" className="terms-section">
            <h2>28. CALIFORNIA USERS AND RESIDENTS</h2>
            <p>
              If any complaint with us is not satisfactorily resolved, you can
              contact the Complaint Assistance Unit of the Division of Consumer
              Services of the California Department of Consumer Affairs in
              writing at 1625 North Market Blvd., Suite N 112, Sacramento,
              California 95834 or by telephone at (800) 952-5210 or (916)
              445-1254.
            </p>
          </section>

          <section id="miscellaneous" className="terms-section">
            <h2>29. MISCELLANEOUS</h2>
            <p>
              These Legal Terms and any policies or operating rules posted by us
              on the Services or in respect to the Services constitute the
              entire agreement and understanding between you and us. Our failure
              to exercise or enforce any right or provision of these Legal Terms
              shall not operate as a waiver of such right or provision.
            </p>
          </section>

          <section id="contact-us" className="terms-section">
            <h2>30. CONTACT US</h2>
            <p>
              In order to resolve a complaint regarding the Services or to
              receive further information regarding use of the Services, please
              contact us at:
            </p>
            <address>
              <strong>Morph Technologies LLC</strong>
              <br />
              2690 152nd Ave NE
              <br />
              Unit 313
              <br />
              Redmond, WA 98052
              <br />
              United States
              <br />
              <strong>Phone:</strong>{' '}
              <a href="tel:1-213-357-3951">1-213-357-3951</a>
              <br />
              <strong>Email:</strong>{' '}
              <a href="mailto:info@morph-technologies.com">
                info@morph-technologies.com
              </a>
            </address>
          </section>
        </div>
      </div>
      
      {showScrollTop && (
        <button className="scroll-to-top" onClick={scrollToTop}>
          ↑
        </button>
      )}
    </div>
  );
};

export default TermsConditions;
